import { ErrorInfo, PropsWithChildren, PureComponent } from 'react';
import { isBrowserRelatedError } from 'shared/utils/browserCompat';

import GeneralErrorScreen from './GeneralErrorScreen';

export type ErrorBoundaryProps = PropsWithChildren<unknown>;

interface State {
    hasError: boolean;
    suggestFallback: boolean;
}

/**
 * As whole application isn't wrapped with single ErrorBoundary instance
 * you should wrap every single route with it.
 * Wrapping root route will need error reset on location change and this
 * will turn into issues related to root components rerender. E.g. multiple `useFetchTenant` calls.
 */
class ErrorBoundary extends PureComponent<ErrorBoundaryProps, State> {

    public state: State = {
        hasError: false,
        suggestFallback: false
    };

    public static getDerivedStateFromError(): State {
        return { hasError: true, suggestFallback: false };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // eslint-disable-next-line no-console
        console.error('Uncaught error:', error, errorInfo);

        // Check if error might be browser-related
        const isPossiblyBrowserIssue = isBrowserRelatedError(error);

        if (isPossiblyBrowserIssue) {
            this.setState({ suggestFallback: true });
        }
    }

    public render() {
        const { hasError, suggestFallback } = this.state;
        const { children } = this.props;

        if (hasError) {
            return <GeneralErrorScreen showFallbackLink={ suggestFallback } />;
        }

        return children;
    }

}

export default ErrorBoundary;
